import { ChangeEvent, MouseEvent, useEffect, useRef, useState } from "react";
import styles from "./Search.module.css";
import Icon from "./Icon";

export type MapSearchType = "keyword" | "title" | "description" | "username" | "default";
export type FAQSearchType = "0" | "1" | "2" | "3" | "4" | "5" | "6";
export type SearchType = MapSearchType | FAQSearchType;

/**
 * 검색 조건 설정
 * @interface
 * @prop {string} keyword 검색어
 * @prop {{@link MapSearchType}} filter (optional) 검색어 필터
 */
export interface SearchOptions {
    keyword: string;
    filter?: SearchType;
}

/**
 * @author khy
 */
export interface SearchFilters {
    value: SearchType;
    name: string;
}

/**
 * 검색 설정
 * @interface
 * @prop {(options: {@link SearchOptions}) => void} doSearch 검색 버튼 클릭 시 이벤터핸들러 함수
 * @prop {(options: {@link SearchOptions}) => void} onChange (optional) 검색어 입력 시 이벤트핸들러 함수
 * @prop {string} label (optional) 제목 라벨
 * @prop {{@link SearchFilters}[]} filters (optional) 검색 필터 옵션
 * @prop onFilterChange (optional) 필터 변경시 동작 함수 (현재 FAQ페이지 사용)
 * @prop selectedFilter (optional) 선택된 option 지정
 * @prop {"header"|"side"|"footer"} type (optional) 컴포넌트 타입 (ex. header: 지원문의 | side: 사이드시트 | footer: 지도목록)
 * @prop {string} placeholder (optional) placeholder 문자열
 * @prop {any[]} suggest (optional) 검색어 자동완성 목록
 */
interface SearchProps {
    doSearch: (options: SearchOptions) => void;
    onChange?: (options: SearchOptions) => void;
    label?: string;
    filters?: SearchFilters[];
    onFilterChange?: (filterValue: string) => void;
    selectedFilter?: FAQSearchType;
    type?: "header" | "header2" | "side" | "footer";
    placeholder?: string;
    suggest?: any[];
}

const debounce = <T extends (...args: any[]) => any>(fn: T, delay: number) => {
    let timeout: ReturnType<typeof setTimeout>;

    return (...args: Parameters<T>): ReturnType<T> => {
        let result: any;
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
            result = fn(...args);
        }, delay);
        return result;
    };
};

/**
 * 지도 검색 컴포넌트
 * @author mjkang
 * @date 2024-08-29 15:14:43
 * @param props {@link SearchProps}
 * @returns Search
 */
const Search = (props: SearchProps) => {
    const defaultPlaceholder = props.placeholder ?? "검색어를 입력해 주세요.";

    // 필터에 따른 placeholder 텍스트를 결정하는 함수
    const getPlaceholderByFilter = (filter?: SearchType): string => {
        if (!filter || filter === "default") return "전체 검색어를 입력해 주세요.";

        // 필터 값에 따른 placeholder 매핑 - 더 간결한 표현으로 개선
        const placeholderMap: Record<string, string> = {
            // 지도 검색 관련
            keyword: "키워드",
            title: "지도명",
            description: "설명",
            username: "별명",
            // FAQ 검색 관련
            "0": "전체",
            "1": "지도만들기",
            "2": "지도 사용하기",
            "3": "Pro 사용",
            "4": "가격문의",
            "5": "회사정책",
            "6": "기타",
        };

        return placeholderMap[filter] + " 검색어를 입력해 주세요" || defaultPlaceholder;
    };

    const [inputValue, _setInputValue] = useState("");
    const [inputValueRef, setInputValue] = [
        useRef(inputValue),
        (value: string) => {
            inputValueRef.current = value;
            _setInputValue(value);
        },
    ];

    const [selectValue, _setSelectValue] = useState<SearchType>();
    const [selectValueRef, setSelectValue] = [
        useRef(selectValue),
        (value: SearchType) => {
            selectValueRef.current = value;
            _setSelectValue(value);
        },
    ];

    // 현재 선택된 필터 기반 placeholder
    const [currentPlaceholder, setCurrentPlaceholder] = useState(defaultPlaceholder);

    const [isSelected, setIsSelected] = useState<boolean>(false);

    const debouncedOnChange = props.onChange ? debounce(props.onChange, 300) : undefined;

    useEffect(() => {
        document.addEventListener("keydown", onEnter);

        return () => {
            document.removeEventListener("keydown", onEnter);
        };
    }, []);

    useEffect(() => {
        debouncedOnChange &&
            debouncedOnChange({
                filter: selectValue,
                keyword: isSelected ? "" : inputValue,
            });

        isSelected && setIsSelected(false);
    }, [inputValue]);

    useEffect(() => {
        if (isSelected && debouncedOnChange) {
            debouncedOnChange({
                filter: selectValue,
                keyword: "",
            });

            setIsSelected(false);
        }
    }, [isSelected]);

    // 셀렉트박스 필터 선택된 경우(FAQ 이용중) 또는 초기 설정
    useEffect(() => {
        if (props.selectedFilter !== undefined) {
            _setSelectValue(props.selectedFilter);
            selectValueRef.current = props.selectedFilter;
            // 필터 변경에 따른 placeholder 업데이트
            setCurrentPlaceholder(getPlaceholderByFilter(props.selectedFilter));
        } else if (props.filters && props.filters.length > 0) {
            // 초기 필터 값 설정
            const initialFilter = props.filters[0].value;
            _setSelectValue(initialFilter);
            selectValueRef.current = initialFilter;
            // 초기 placeholder 설정
            setCurrentPlaceholder(getPlaceholderByFilter(initialFilter));
        }
    }, [props.selectedFilter]);

    const onEnter = (e: KeyboardEvent) => {
        e.key === "Enter" && handleClick();
    };

    /**
     * input & select onChange 이벤트 처리 함수
     */
    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // e.currentTarget.name === "select" ? setSelectValue(e.currentTarget.value as MapSearchType) : setInputValue(e.currentTarget.value);
        if (e.currentTarget.name === "select") {
            const value = e.currentTarget.value as SearchType;
            setSelectValue(value);

            // placeholder 업데이트
            setCurrentPlaceholder(getPlaceholderByFilter(value));

            // 고객지원 페이지 처리
            props.onFilterChange?.(value); // 필터 변경 시 동작
        } else {
            setInputValue(e.currentTarget.value);
        }
    };

    /**
     * 검색 버튼 클릭 시 검색 실행 함수
     */
    const handleClick = () => {
        const searchOptions: SearchOptions = {
            keyword: inputValueRef.current,
        };

        if (props.filters) {
            searchOptions["filter"] = selectValueRef.current;
        }

        props.doSearch(searchOptions);
    };

    const selectKeyword = (e: MouseEvent<HTMLDivElement>) => {
        setIsSelected(true);
        setInputValue(e.currentTarget.innerHTML);
    };

    return (
        <div className={`${styles.wrapper} ${props.type ? styles[props.type] : ""}`}>
            {props.label && <span className={styles.title}>{props.label}</span>}
            <div className={styles.search}>
                {props.filters && (
                    <select name="select" value={selectValue} onChange={handleChange}>
                        {props.filters.map((option: SearchFilters) => {
                            return (
                                <option key={option.value} value={option.value}>
                                    {option.name}
                                </option>
                            );
                        })}
                    </select>
                )}
                <div className={styles.container}>
                    <input name="input" placeholder={currentPlaceholder} value={inputValue} onChange={handleChange} />
                    <div className={styles.icon} onClick={handleClick}>
                        <Icon name="search" />
                    </div>
                </div>
            </div>
            {props.suggest && props.suggest.length !== 0 && (
                <div className={styles.suggest}>
                    {props.suggest.map((item, idx) => {
                        return (
                            <div className={styles.item} key={idx} onClick={selectKeyword}>
                                {item}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Search;
